import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useLanguage } from '../../../contexts/LanguageContext';
import { MainStackParamList } from '../../../navigation/MainNavigator';
import { FONTS } from '../../../constants/config';
import { createShadow } from '../../../utils/styles';
import { Toast } from '../../../components';
import { SpecializationProgram } from '../types';

const FOREGROUND = '#414042';

interface ProgramsSectionProps {
  programs: SpecializationProgram[];
  specializationId?: number;
  entityName?: string;
  onViewProgram?: (programId: number) => void;
  onAddProgram?: () => void;
  readOnly?: boolean;
}

type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

function ProgramCard({ program, entityName }: { program: SpecializationProgram; entityName?: string }) {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const navigation = useNavigation<NavigationProp>();

  return (
    <View style={cardStyles.container}>
      <Text style={cardStyles.title}>
        {program.name}
      </Text>

      {/* Progress bar row */}
      <View style={cardStyles.progressRow}>
        {/* Percentage pill */}
        <View style={cardStyles.pctPill}>
          <Text style={cardStyles.pctText}>{program.progress}</Text>
          <Text style={cardStyles.pctText}>%</Text>
        </View>

        {/* Progress track — fill from RIGHT for RTL */}
        <View style={cardStyles.trackBg}>
          {/* Green fill anchored to the right */}
          <View
            style={[
              cardStyles.trackFill,
              { width: `${program.progress}%` },
            ]}
          />
          {/* Green circle at junction */}
          <View
            style={[
              cardStyles.progressDot,
              { right: `${program.progress}%` },
            ]}
          />
        </View>
      </View>

      {/* View Details */}
      <TouchableOpacity
        style={cardStyles.linkRow}
        onPress={() => navigation.navigate('ProjectDetail', { name: program.name, id: program.id, entityName })}
      >
        <Ionicons name="chevron-back" size={16} color={FOREGROUND} />
        <Text style={cardStyles.linkText}>
          {isRTL ? 'عرض التفاصيل' : 'View Details'}
        </Text>
      </TouchableOpacity>
    </View>
  );
}

export default function ProgramsSection({ programs, specializationId, entityName, onViewProgram, onAddProgram, readOnly }: ProgramsSectionProps) {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const navigation = useNavigation<NavigationProp>();
  const [toastVisible, setToastVisible] = useState(false);

  const handleAddProgram = () => {
    if (onAddProgram) { onAddProgram(); return; }
    if (specializationId) {
      navigation.navigate('AddProject', { specializationId });
      return;
    }
    setToastVisible(true);
  };

  return (
    <View
      style={[
        styles.container,
        createShadow(0, 1, 2, '#000', 0.1),
      ]}
    >
      {/* Header */}
      <View style={styles.header}>
        {!readOnly && (
          <TouchableOpacity
            style={[styles.addBtn, createShadow(0, 1, 3, '#000', 0.1)]}
            onPress={handleAddProgram}
          >
            <Text style={styles.addBtnText}>
              {isRTL ? 'إضافة مشروع' : 'Add Program'}
            </Text>
            <Ionicons name="add-circle-outline" size={14} color={FOREGROUND} />
          </TouchableOpacity>
        )}
        <Text style={styles.sectionTitle}>
          {isRTL ? 'البرامج والمشاريع' : 'Programs & Projects'}
        </Text>
      </View>

      {/* Cards — Figma uses 4-column grid: 1 spacer + 3 visible cards */}
      <View style={[styles.cardsRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
        {programs.map((program) => (
          <ProgramCard key={program.id} program={program} entityName={entityName} />
        ))}
        <View style={styles.cardSpacer} />
      </View>
      <Toast visible={toastVisible} message={isRTL ? 'قريباً' : 'Coming soon'} onHide={() => setToastVisible(false)} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderColor: '#e2e8f0',
    borderRadius: 8,
    borderWidth: 1,
    padding: 12,
    gap: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    width: '100%',
  },
  sectionTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'right',
    color: FOREGROUND,
    flex: 1,
    lineHeight: 20,
  },
  addBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    minHeight: 32,
  },
  addBtnText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'center',
    color: FOREGROUND,
  },
  cardsRow: {
    gap: 20,
    borderRadius: 8,
    padding: 12,
    backgroundColor: '#f1f5f9',
  },
  cardSpacer: {
    flex: 1,
  },
});

const cardStyles = StyleSheet.create({
  container: {
    width: '23%',
    backgroundColor: '#FFFFFF',
    borderColor: '#e2e8f0',
    borderRadius: 8,
    borderWidth: 1,
    padding: 12,
    gap: 16,
  },
  title: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'right',
    color: FOREGROUND,
    lineHeight: 20,
  },
  progressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  pctPill: {
    backgroundColor: '#f1f5f9',
    borderRadius: 100,
    paddingHorizontal: 8,
    paddingVertical: 2,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
  },
  pctText: {
    fontSize: 14,
    fontFamily: FONTS.numbers,
    fontWeight: '600',
    color: FOREGROUND,
    textAlign: 'center',
  },
  trackBg: {
    flex: 1,
    height: 8,
    borderRadius: 8,
    backgroundColor: '#f1f5f9',
    overflow: 'visible',
    flexDirection: 'row-reverse',
    alignItems: 'center',
  },
  trackFill: {
    height: 8,
    borderRadius: 8,
    backgroundColor: '#2ca02c',
  },
  progressDot: {
    position: 'absolute',
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: '#2ca02c',
    borderWidth: 2,
    borderColor: '#FFFFFF',
    transform: [{ translateX: 7 }],
  },
  linkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    justifyContent: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
    minHeight: 24,
  },
  linkText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    textAlign: 'right',
    color: FOREGROUND,
    lineHeight: 14,
  },
});
